import classNames from 'classnames';
import { forwardRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Else, If, Then, When } from 'react-if';

import { useTextAreaService } from './text-area.service';
import { sizeClasses, TextAreaProps } from './text-area.types';

export const TextArea: React.FC<TextAreaProps> = forwardRef<
  HTMLTextAreaElement,
  TextAreaProps
>(
  (
    {
      id,
      className,
      labelKey,
      size = 'medium',
      error,
      RightIcon,
      LeftIcon,
      helperTextKey,
      helperTextValue,
      value,
      onChange,
      ...rest
    },
    ref
  ) => {
    const { innerRef } = useTextAreaService({ ref, value });
    const { t } = useTranslation();
    const hasError = !!error;
    const containerClasses = classNames(
      'textarea-field-wrapper',
      className,
      {
        [sizeClasses[size]]: size,
      },
      { 'has-error': hasError }
    );

    const labelClasses = classNames('textarea-field-label', {
      'left-icon': LeftIcon,
    });

    const textareaClasses = classNames(
      'textarea-field',
      { 'left-icon': LeftIcon },
      { 'right-icon': RightIcon }
    );

    const textareaId = id || rest.name;

    return (
      <div className={containerClasses}>
        <div className="textarea-field-content">
          {LeftIcon && (
            <LeftIcon className="textarea-field-icon textarea-field-icon-left" />
          )}
          <textarea
            ref={innerRef}
            id={textareaId}
            className={textareaClasses}
            placeholder=" "
            value={value}
            onChange={e => {
              onChange?.(e);
            }}
            {...rest}
          />
          {RightIcon && (
            <RightIcon className="textarea-field-icon textarea-field-icon-right" />
          )}
          <When condition={labelKey !== undefined}>
            <label htmlFor={textareaId} className={labelClasses}>
              <Trans t={t}>{labelKey}</Trans>
            </label>
          </When>
        </div>
        <When condition={helperTextValue !== undefined}>
          <p className="textarea-field-helper-text">
            <If condition={hasError}>
              <Then>
                <Trans t={t} values={helperTextValue}>
                  {error}
                </Trans>
              </Then>
              <Else>
                <Trans t={t} values={helperTextValue}>
                  {helperTextKey}
                </Trans>
              </Else>
            </If>
          </p>
        </When>
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';

export default TextArea;
